import { useEffect, useState } from "react";
import { Euro, TrendingUp, Wallet, AlertTriangle, Receipt } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";

const SEV_STYLE = {
  expired: "bg-rose-100 text-rose-700 border-rose-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  notice: "bg-blue-50 text-blue-700 border-blue-200",
};

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}><Icon size={18} strokeWidth={1.5} /></span>
      </div>
      <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Breakdown({ title, items }) {
  const { t } = useI18n();
  const max = Math.max(1, ...items.map((i) => Math.abs(i.value)));
  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-testid="financial-breakdown">
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">{title}</h3>
      {items.length === 0 ? <p className="text-sm text-slate-400">{t("common.noData")}</p> : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.label}>
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-600 capitalize truncate">{i.label}</span><span className="font-medium text-slate-800">{money(i.value)}</span></div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-800 rounded-full" style={{ width: `${(Math.abs(i.value) / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Financial() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/financial").then(({ data }) => setData(data)).catch(() => {});
    api.get("/compliance/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="financial-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.financial")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("sub.financial")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Euro} label={t("stat.revenue")} value={data ? money(data.revenue) : "—"} accent="bg-emerald-50 text-emerald-700" testid="fin-revenue" />
        <Kpi icon={Wallet} label={t("fin.partnerPayouts")} value={data ? money(data.partner_payouts_total ?? data.pending_company_payments) : "—"} accent="bg-rose-50 text-rose-700" testid="fin-payouts" />
        <Kpi icon={Receipt} label={t("fin.expenses")} value={data ? money(data.expenses_total ?? 0) : "—"} accent="bg-amber-50 text-amber-700" testid="fin-expenses" />
        <Kpi icon={TrendingUp} label={t("fin.netProfit")} value={data ? money(data.net_profit ?? data.profit) : "—"} accent="bg-blue-50 text-blue-700" testid="fin-net-profit" />
      </div>
      <p className="text-xs text-slate-400 -mt-2">{t("fin.netProfit")} = {t("stat.revenue")} − {t("fin.partnerPayouts")} − {t("fin.expenses")}</p>

      {alerts.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-testid="compliance-alerts">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> {t("fin.vehicleExpiry")}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {alerts.map((a) => (
              <div key={`${a.plate}-${a.type}`} className={`rounded-lg border px-3 py-2 text-sm ${SEV_STYLE[a.severity]}`} data-testid="compliance-alert">
                <div className="font-semibold">{a.plate} · {a.type}</div>
                <div className="text-xs">{a.days_left < 0 ? `${-a.days_left}d` : `${a.days_left}d`} ({a.expiry})</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Breakdown title={t("fin.byCountry")} items={data?.profit_by_country || []} />
        <Breakdown title={t("fin.byRoute")} items={data?.profit_by_route || []} />
        <Breakdown title={t("fin.byCompany")} items={data?.profit_by_company || []} />
        <Breakdown title={t("fin.byDriver")} items={data?.profit_by_driver || []} />
      </div>
    </div>
  );
}
